/**
 * Utility functions for Sentry error handling
 */

/**
 * Enum for error context names
 * @enum {string}
 */
export const ErrorContextName = Object.freeze({
  API_REQUEST: 'API Request',
  VALIDATION: 'Validation',
  INTEGRATION: 'Integration',
  ELASTIC_SEARCH: 'ElasticSearch',
  DATABASE: 'Database',
  NETWORK: 'Network',
  AUTHENTICATION: 'Authentication',
  AUTHORIZATION: 'Authorization',
  UNKNOWN: 'Unknown'
});

/**
 * Captures an error in Sentry with additional context
 * @param {Error} error - The error object to capture
 * @param {Object} options - Additional options for error reporting
 * @param {string} options.errorType - Type of error (e.g., 'ElasticSearch Failure')
 * @param {Object} options.context - Additional context data
 * @param {string} options.contextName - Name of the context (e.g., 'ElasticSearch')
 * @param {Object} options.tags - Additional tags to set
 */
export const captureSentryError = (error, options = {}) => {
  if (!(error instanceof Error)) {
    console.warn('captureSentryError called with non-Error object:', error);
    return;
  }

  // Check if Sentry is configured
  if (window?.Sentry?.captureException) {
    window.Sentry.withScope((scope) => {
      // Set error type tag
      if (options.errorType) {
        scope.setTag('error_type', options.errorType);
      }

      // Set context if provided
      if (options.context && options.contextName) {
        scope.setContext(options.contextName, {
          ...options.context,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }

      // Set additional tags if provided
      if (options.tags) {
        Object.entries(options.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      window.Sentry.captureException(error);
    });
  } else {
    // Fall back to console.error when Sentry is not available
    const errorContext = options.contextName ? `[${options.contextName}]` : '';
    const errorType = options.errorType ? `(${options.errorType})` : '';
    console.error(`${errorContext} ${errorType} Error:`, error, options.context || {});
  }
};

/**
 * Captures an API error in Sentry
 * @param {Error} error - The error object to capture
 * @param {Object} [options] - Additional options for error reporting
 * @param {string} [options.endpoint] - The API endpoint that caused the error
 */
export const captureAPIError = (error, { endpoint } = {}) => {

  captureSentryError(error, {
    errorType: 'API Error',
    contextName: ErrorContextName.API_REQUEST,
    context: { 
      url: window.location.href,
      ...(endpoint && { endpoint })
    }
  });
};

/**
 * Captures a validation error in Sentry
 * @param {Error} error - The error object to capture
 */
export const captureValidationError = (error) => {

  captureSentryError(error, {
    errorType: 'Validation Error',
    contextName: ErrorContextName.VALIDATION,
    context: { url: window.location.href }
  });
};

/**
 * Captures an integration error in Sentry
 * @param {Error} error - The error object to capture
 */
export const captureIntegrationError = (error) => {

  captureSentryError(error, {
    errorType: 'Integration Error',
    contextName: ErrorContextName.INTEGRATION,
    context: { url: window.location.href }
  });
};